import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation, useI18nStore } from '../lib/i18n';
import { useTheme } from '../hooks/useTheme';
import { useDomain } from '../lib/domain';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Globe, Moon, Sun, ArrowLeft, Mail, KeyRound, ShieldCheck, Loader2, Eye, EyeOff, Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LANGUAGES = [
  { code: 'tr', name: 'Türkçe', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'en', name: 'English', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'nl', name: 'Nederlands', flag: '\u{1F1F3}\u{1F1F1}' },
  { code: 'fr', name: 'Français', flag: '\u{1F1EB}\u{1F1F7}' }
];

/* ── colour palette ── */
const palette = {
  dark: {
    node: [168, 85, 247],
    line: 'rgba(168,85,247,0.12)',
    pulse: 'rgba(168,85,247,0.35)',
    glow: 'rgba(168,85,247,0.06)',
  },
  light: {
    node: [109, 40, 217],
    line: 'rgba(109,40,217,0.08)',
    pulse: 'rgba(109,40,217,0.20)',
    glow: 'rgba(109,40,217,0.04)',
  },
};

/* ───── Circuit Board Canvas ───── */
function CircuitCanvas({ isDark }) {
  const ref = useRef(null);
  const colRef = useRef(isDark ? palette.dark.node : palette.light.node);

  useEffect(() => {
    colRef.current = isDark ? palette.dark.node : palette.light.node;
  }, [isDark]);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let animId;
    const nodes = [];
    const COUNT = 40;

    const resize = () => { c.width = c.offsetWidth; c.height = c.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      nodes.push({
        x: Math.random() * c.width, y: Math.random() * c.height,
        targetX: Math.random() * c.width, targetY: Math.random() * c.height,
        size: 2 + Math.random() * 3, pulse: Math.random() * Math.PI * 2,
        speed: 0.002 + Math.random() * 0.003,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      const [r, g, b] = colRef.current;

      /* circuit traces */
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const nearest = nodes.reduce((best, other, j) => {
          if (j === i) return best;
          const d = Math.hypot(n.x - other.x, n.y - other.y);
          return d < best.d ? { d, n: other } : best;
        }, { d: 200, n: null });

        if (nearest.n) {
          ctx.beginPath();
          const midX = (n.x + nearest.n.x) / 2;
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(midX, n.y);
          ctx.lineTo(midX, nearest.n.y);
          ctx.lineTo(nearest.n.x, nearest.n.y);
          ctx.strokeStyle = `rgba(${r},${g},${b},${(1 - nearest.d / 200) * 0.15})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      /* nodes */
      nodes.forEach(n => {
        n.x += (n.targetX - n.x) * n.speed;
        n.y += (n.targetY - n.y) * n.speed;
        if (Math.abs(n.x - n.targetX) < 2) n.targetX = Math.random() * c.width;
        if (Math.abs(n.y - n.targetY) < 2) n.targetY = Math.random() * c.height;
        n.pulse += 0.025;

        const glow = 0.4 + Math.sin(n.pulse) * 0.3;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${glow})`;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.size * 4, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${glow * 0.08})`;
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
}

/* ───── Diamond Grid ───── */
function DiamondGrid({ isDark }) {
  const stroke = isDark ? palette.dark.line : palette.light.line;
  const items = useRef(Array.from({ length: 10 }, (_, i) => ({
    id: i, left: `${8 + Math.random() * 84}%`, top: `${5 + Math.random() * 90}%`,
    delay: `${Math.random() * 8}s`, size: 16 + Math.random() * 32, dur: `${10 + Math.random() * 10}s`,
  }))).current;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {items.map(d => (
        <div key={d.id} className="absolute animate-float-hex" style={{ left: d.left, top: d.top, animationDelay: d.delay, animationDuration: d.dur }}>
          <svg width={d.size} height={d.size} viewBox="0 0 100 100" fill="none">
            <rect x="15" y="15" width="70" height="70" rx="4" stroke={stroke} strokeWidth="1.5" transform="rotate(45 50 50)" fill="none" />
          </svg>
        </div>
      ))}
    </div>
  );
}

/* ───── Pulse Ring ───── */
function PulseRing({ isDark }) {
  const color = isDark ? palette.dark.pulse : palette.light.pulse;
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
      <div className="w-[600px] h-[600px] rounded-full border animate-ping" style={{ borderColor: color, animationDuration: '4s' }} />
      <div className="absolute w-[400px] h-[400px] rounded-full border animate-ping" style={{ borderColor: color, animationDuration: '5s', animationDelay: '1s' }} />
    </div>
  );
}

/* ══════════ MAIN ══════════ */
const ForgotPassword = () => {
  const { t } = useTranslation();
  const { language, setLanguage } = useI18nStore();
  const { theme, toggleTheme } = useTheme();
  const { settings } = useSystemSettings();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const bgMain = isDark ? 'bg-slate-950' : 'bg-slate-50';
  const textPrimary = isDark ? 'text-white' : 'text-slate-900';
  const textSecondary = isDark ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDark ? 'bg-slate-900/80 border-slate-800/60' : 'bg-white/90 border-slate-200/60';
  const inputBg = isDark ? 'bg-slate-800/60 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-slate-200 text-slate-900';
  const accentColor = isDark ? 'text-violet-400' : 'text-violet-600';
  const btnBg = isDark ? 'bg-violet-600 hover:bg-violet-500' : 'bg-violet-600 hover:bg-violet-700';

  const stepIcons = [
    <Mail className="h-5 w-5" />,
    <KeyRound className="h-5 w-5" />,
    <Lock className="h-5 w-5" />,
  ];
  const stepLabels = [
    t('forgotPassword.step1Label'),
    t('forgotPassword.step2Label'),
    t('forgotPassword.step3Label'),
  ];

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('forgotPassword.enterEmail')); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setError(t('forgotPassword.invalidEmail')); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success(t('forgotPassword.codeSent'));
      setStep(2);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.response?.data?.message || 'Error'));
    }
    setLoading(false);
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim() || code.length < 6) { setError(t('forgotPassword.enterValidCode')); return; }
    setLoading(true); setError('');
    try {
      const res = await api.post('/auth/verify-reset-code', { email, code });
      setVerificationToken(res.data?.data?.verification_token || code);
      toast.success(t('forgotPassword.codeVerified'));
      setStep(3);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.response?.data?.message || t('forgotPassword.invalidCode')));
    }
    setLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError(t('forgotPassword.passwordTooShort')); return; }
    if (newPassword !== confirmPassword) { setError(t('validation.passwordMatch')); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', { email, code: verificationToken, new_password: newPassword, new_password_confirm: confirmPassword });
      toast.success(t('forgotPassword.successTitle'));
      setStep(4);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : (err.response?.data?.message || 'Error'));
    }
    setLoading(false);
  };

  return (
    <div className={`min-h-screen ${bgMain} relative overflow-hidden transition-colors duration-500`} data-testid="forgot-password-page">
      {/* Animated Background */}
      <CircuitCanvas isDark={isDark} />
      <DiamondGrid isDark={isDark} />
      <PulseRing isDark={isDark} />

      {/* CSS Animations */}
      <style>{`
        @keyframes float-hex { 0%,100% { transform: translateY(0) rotate(0deg); opacity:.5; } 50% { transform: translateY(-18px) rotate(8deg); opacity:.8; } }
        .animate-float-hex { animation: float-hex 10s ease-in-out infinite; }
      `}</style>

      {/* Top Bar */}
      <div className="relative z-20 flex items-center justify-between p-6">
        <Link to="/login" className={`flex items-center gap-2 ${textSecondary} hover:${textPrimary} transition-colors`} data-testid="forgot-back-to-login">
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm">{t('auth.backToLogin')}</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggleTheme} className={`${textSecondary} hover:${textPrimary}`} data-testid="forgot-theme-toggle">
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className={`gap-2 ${textSecondary}`}>
                <Globe className="h-4 w-4" />
                {LANGUAGES.find(l => l.code === language)?.flag}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGUAGES.map(l => (
                <DropdownMenuItem key={l.code} onClick={() => setLanguage(l.code)} className={language === l.code ? 'bg-violet-50 dark:bg-violet-950' : ''}>
                  <span className="mr-2">{l.flag}</span>{l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-[calc(100vh-80px)] px-4">
        <div className={`w-full max-w-md rounded-2xl border backdrop-blur-xl p-8 shadow-2xl ${cardBg}`}>

          {/* Progress Steps */}
          {step < 4 && (
            <div className="flex items-center justify-center gap-2 mb-8" data-testid="forgot-progress-steps">
              {[1, 2, 3].map((s, i) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${
                    step >= s
                      ? `${btnBg} text-white shadow-lg`
                      : isDark ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'
                  }`}>
                    {step > s ? <Check className="h-4 w-4" /> : stepIcons[i]}
                  </div>
                  {i < 2 && (
                    <div className={`w-12 h-0.5 rounded transition-colors duration-300 ${
                      step > s
                        ? 'bg-violet-500'
                        : isDark ? 'bg-slate-800' : 'bg-slate-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Step 1: Email */}
          {step === 1 && (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div className="text-center mb-2">
                <div className={`mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}>
                  <Mail className={`h-7 w-7 ${accentColor}`} />
                </div>
                <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('forgotPassword.title')}</h1>
                <p className={`text-sm mt-2 ${textSecondary}`}>{t('forgotPassword.subtitle')}</p>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>{t('auth.email')}</Label>
                <Input
                  type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  className={`h-12 ${inputBg}`}
                  data-testid="forgot-email-input"
                />
              </div>
              <p className={`text-xs ${textSecondary}`}>{t('forgotPassword.emailHint')}</p>
              {error && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-2 rounded">{error}</p>}
              <Button type="submit" className={`w-full h-12 ${btnBg} text-white font-medium`} disabled={loading} data-testid="forgot-send-code-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                {t('forgotPassword.sendCode')}
              </Button>
            </form>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div className="text-center mb-2">
                <div className={`mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}>
                  <KeyRound className={`h-7 w-7 ${accentColor}`} />
                </div>
                <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('forgotPassword.verifyCode')}</h1>
                <p className={`text-sm mt-2 ${textSecondary}`}>{t('forgotPassword.codeSentEmail')}</p>
                <p className={`text-xs mt-1 font-medium ${accentColor}`}>{email}</p>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>{t('forgotPassword.enterCode')}</Label>
                <Input
                  type="text" inputMode="numeric" maxLength={6} value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                  placeholder="000000"
                  className={`h-14 text-center text-2xl font-mono tracking-[0.5em] ${inputBg}`}
                  data-testid="forgot-code-input"
                />
                <p className={`text-xs ${textSecondary}`}>{t('forgotPassword.codeExpiry')}</p>
              </div>
              {error && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-2 rounded">{error}</p>}
              <Button type="submit" className={`w-full h-12 ${btnBg} text-white font-medium`} disabled={loading} data-testid="forgot-verify-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                {t('forgotPassword.verify')}
              </Button>
              <button type="button" onClick={() => { setCode(''); handleSendCode({ preventDefault: () => {} }); }} className={`w-full text-sm ${accentColor} hover:underline`} data-testid="forgot-resend-btn">
                {t('forgotPassword.resendCode')}
              </button>
            </form>
          )}

          {/* Step 3: New Password */}
          {step === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="text-center mb-2">
                <div className={`mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-violet-500/10' : 'bg-violet-50'}`}>
                  <Lock className={`h-7 w-7 ${accentColor}`} />
                </div>
                <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('forgotPassword.newPassword')}</h1>
                <p className={`text-sm mt-2 ${textSecondary}`}>{t('forgotPassword.newPasswordDesc')}</p>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>{t('forgotPassword.newPassword')}</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'} value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    className={`h-12 pr-10 ${inputBg}`}
                    data-testid="forgot-new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className={textSecondary}>{t('forgotPassword.confirmPassword')}</Label>
                <Input
                  type="password" value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder="••••••••"
                  className={`h-12 ${inputBg}`}
                  data-testid="forgot-confirm-password-input"
                />
              </div>
              {error && <p className="text-sm text-red-500 bg-red-50 dark:bg-red-950/30 p-2 rounded">{error}</p>}
              <Button type="submit" className={`w-full h-12 ${btnBg} text-white font-medium`} disabled={loading} data-testid="forgot-reset-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
                {t('forgotPassword.resetPassword')}
              </Button>
            </form>
          )}

          {/* Step 4: Success */}
          {step === 4 && (
            <div className="text-center space-y-5" data-testid="forgot-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <ShieldCheck className="h-8 w-8 text-emerald-500" />
              </div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('forgotPassword.successTitle')}</h1>
              <p className={`${textSecondary}`}>{t('forgotPassword.successDesc')}</p>
              <Link to="/login">
                <Button className={`w-full h-12 ${btnBg} text-white font-medium mt-4`} data-testid="forgot-go-login-btn">
                  {t('common.login')}
                </Button>
              </Link>
            </div>
          )}

          {/* Bottom Link */}
          {step < 4 && (
            <p className={`text-center text-sm mt-6 ${textSecondary}`}>
              {t('forgotPassword.rememberPassword')}{' '}
              <Link to="/login" className={`font-medium ${accentColor} hover:underline`}>
                {t('common.login')}
              </Link>
            </p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className={`relative z-10 text-center pb-6 text-xs ${textSecondary} flex items-center justify-center gap-2`}>
        <Lock className="h-3 w-3" />
        {t('auth.twoFASecureConnection')}
      </div>
    </div>
  );
};

export default ForgotPassword;
